import type { RelatedPharmacy } from '@/entities/business/api/business.api';
import type { PartnerCompanyOption } from '@/entities/partner-company/api/partner-company.api';
import {
  DRUG_APPROVAL_STATUS_OPTIONS,
  PHARMA_TRANSACTION_STATUS_OPTIONS,
  formatStatusLabel,
  getClinicTypeLabel,
  getDrugApprovalStatusColor,
  getDrugApprovalStatusLabel,
  getTransactionStatusColor,
  mapContractStatusToDrugApproval,
} from '@/pages/clients/clientStatusUtils';
import { formatNameWithClosedOn } from '@/shared/utils/facilityClosure';

export {
  formatStatusLabel,
  getClinicTypeLabel,
  getDrugApprovalStatusColor,
  getDrugApprovalStatusLabel,
  getTransactionStatusColor,
  mapContractStatusToDrugApproval,
};

/**
 * 문전약국 Badge 표시. 서버 RelatedPharmacy.display 와 동일 형식.
 * 폐업일이 있으면 `약국명(폐업 yyyy-MM-dd)`.
 */
export const formatRelatedPharmacyLabel = (
  pharmacy: RelatedPharmacy | string,
): string => {
  if (typeof pharmacy === 'string') return pharmacy;
  return formatNameWithClosedOn(
    pharmacy.pharmacyName ?? '',
    pharmacy.pharmacyClosedOn,
  );
};

/**
 * 레거시 clientInfo 거래 상태 라디오.
 * 변경 시 transactionStatus + drugApprovalStatus를 함께 세팅한다.
 */
export const CONTRACT_STATUS_RADIO_OPTIONS = [
  { value: '', label: '전체' },
  { value: '신청', label: '신청' },
  { value: '확인 중', label: '확인 중' },
  { value: '이관 확인 중', label: '이관 확인 중' },
  { value: '승인', label: '승인' },
  { value: '재신청', label: '재신청' },
  { value: '종료', label: '종료' },
  { value: '거절', label: '거절' },
] as const;

/** @deprecated 목록은 CONTRACT_STATUS_RADIO_OPTIONS 사용 */
export const TRANSACTION_STATUS_OPTIONS = [
  { value: '', label: '전체' },
  ...CONTRACT_STATUS_RADIO_OPTIONS.filter((opt) => opt.value !== ''),
] as const;

export const DRUG_STATUS_FILTER_OPTIONS = [
  { value: '', label: '전체' },
  ...DRUG_APPROVAL_STATUS_OPTIONS.map((opt) => ({
    value: opt.value,
    label: opt.label,
  })),
] as const;

export const TRANSACTION_STATUS_EDIT_OPTIONS = PHARMA_TRANSACTION_STATUS_OPTIONS;

export { formatDate } from '@/shared/lib/dateFormat';

/** 쉼표 구분 문자열 → 코드 배열 */
export const parseDrugCodes = (text: string): string[] =>
  text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

/** 코드 배열 → 쉼표 구분 문자열 */
export const joinDrugCodes = (codes?: string[]): string =>
  (codes ?? []).join(', ');

/** 정산업체 선택지 — 상위업체 본인 + 루다링크 */
export const filterSettlementCompanyOptions = (
  options: PartnerCompanyOption[],
  parentCompanyId: string,
): PartnerCompanyOption[] =>
  options.filter(
    (option) =>
      option.partnerCompanyId === Number(parentCompanyId) ||
      option.companyType === 'RUDALINK',
  );

/** 서버가 null을 주면 화면에는 루다링크로 보여 준다 */
export const RUDALINK_DISPLAY_NAME = '루다링크';

export const displayPartnerCompanyName = (
  name?: string | null,
): string => {
  const trimmed = name?.trim();
  return trimmed ? trimmed : RUDALINK_DISPLAY_NAME;
};

/** 미입력 시 기본값 — defaultOption(루다링크) */
export const findDefaultPartnerCompany = (
  options: PartnerCompanyOption[],
): PartnerCompanyOption | undefined =>
  options.find((option) => option.defaultOption) ??
  options.find((option) => option.companyType === 'RUDALINK');

/**
 * 상위업체가 바뀌어 현재 정산 선택이 선택지에서 빠지거나 비어 있으면
 * 루다링크(기본값)로 보여 준다.
 */
export const resolveSettlementCompanyIdAfterParentChange = (
  options: PartnerCompanyOption[],
  nextParentCompanyId: string,
  currentSettlementCompanyId: string,
): string => {
  const nextOptions = filterSettlementCompanyOptions(
    options,
    nextParentCompanyId,
  );
  const stillAllowed = nextOptions.some(
    (option) => String(option.partnerCompanyId) === currentSettlementCompanyId,
  );
  if (stillAllowed) return currentSettlementCompanyId;

  const fallback = findDefaultPartnerCompany(nextOptions);
  return fallback ? String(fallback.partnerCompanyId) : '';
};

/**
 * 상위·정산이 비어 있으면 루다링크로 채운다.
 * 정산은 상위업체 본인 또는 루다링크만 허용한다.
 */
export const resolveCompanyIdsWithDefaults = (
  options: PartnerCompanyOption[],
  parentCompanyId: string,
  settlementCompanyId: string,
): { parentCompanyId: string; settlementCompanyId: string } => {
  const fallback = findDefaultPartnerCompany(options);
  const fallbackId = fallback ? String(fallback.partnerCompanyId) : '';
  const nextParent = parentCompanyId || fallbackId;
  return {
    parentCompanyId: nextParent,
    settlementCompanyId: resolveSettlementCompanyIdAfterParentChange(
      options,
      nextParent,
      settlementCompanyId,
    ),
  };
};

/**
 * PUT …/companies body 조립.
 * 의원급은 drugCode를 넣지 않는다(서버가 DRUG_SCOPE_NOT_ALLOWED).
 * 병원급은 선택한 행의 drugCode가 있으면 의약품 단위로 지정한다.
 */
export const buildBusinessRelationshipCompaniesRequest = ({
  clinicType,
  drugCode,
  parentCompanyId,
  settlementCompanyId,
}: {
  clinicType: 'HOSPITAL' | 'CLINIC' | string;
  drugCode?: string | null;
  parentCompanyId: number;
  settlementCompanyId: number;
}): {
  parentCompanyId: number;
  settlementCompanyId: number;
  drugCode?: string;
} => {
  const body: {
    parentCompanyId: number;
    settlementCompanyId: number;
    drugCode?: string;
  } = {
    parentCompanyId,
    settlementCompanyId,
  };

  const trimmed = drugCode?.trim();
  if (clinicType === 'HOSPITAL' && trimmed) {
    body.drugCode = trimmed;
  }

  return body;
};

/** 상위·정산업체 지정·엑셀 업로드 API 에러 code → 안내 문구 (서버 message 우선) */
export const BUSINESS_RELATIONSHIP_COMPANY_ERROR_MESSAGES = {
  DRUG_SCOPE_NOT_ALLOWED:
    '의원급 거래처는 제약사 단위로만 상위업체·정산업체를 지정할 수 있습니다.',
  BUSINESS_RELATIONSHIP_DRUG_NOT_FOUND:
    '해당 거래처에 등록되지 않은 의약품입니다.',
  CLOSED_CLINIC_NOT_SELECTABLE:
    '폐업한 병의원은 신규 거래처로 등록할 수 없습니다.',
  CLOSED_PHARMACY_NOT_SELECTABLE:
    '폐업한 약국은 거래처에 새로 연결할 수 없습니다.',
} as const;

/**
 * 업체 지정·엑셀 업로드 API 에러 메시지.
 * 알려진 code는 폴백 문구를 쓰고, 그 외는 서버 message / 일반 오류.
 * (삭제된 BUSINESS_RELATIONSHIP_COMPANY_CONFLICT 는 분기하지 않는다)
 */
export const getBusinessRelationshipCompanyErrorMessage = (
  error: unknown,
): string => {
  const data = (
    error as { response?: { data?: { code?: string; message?: string } } }
  )?.response?.data;
  const code = data?.code;
  const message = data?.message?.trim();

  if (code === 'DRUG_SCOPE_NOT_ALLOWED') {
    return (
      message ||
      BUSINESS_RELATIONSHIP_COMPANY_ERROR_MESSAGES.DRUG_SCOPE_NOT_ALLOWED
    );
  }
  if (code === 'BUSINESS_RELATIONSHIP_DRUG_NOT_FOUND') {
    return (
      message ||
      BUSINESS_RELATIONSHIP_COMPANY_ERROR_MESSAGES.BUSINESS_RELATIONSHIP_DRUG_NOT_FOUND
    );
  }
  if (code === 'CLOSED_CLINIC_NOT_SELECTABLE') {
    return (
      message ||
      BUSINESS_RELATIONSHIP_COMPANY_ERROR_MESSAGES.CLOSED_CLINIC_NOT_SELECTABLE
    );
  }
  if (code === 'CLOSED_PHARMACY_NOT_SELECTABLE') {
    return (
      message ||
      BUSINESS_RELATIONSHIP_COMPANY_ERROR_MESSAGES.CLOSED_PHARMACY_NOT_SELECTABLE
    );
  }
  return message || '오류가 발생했습니다.';
};
